import { getInvoiceEffectiveStatus } from "@common/helpers/accrual-source";
import { InvoiceBase, InvoiceStatus } from "@common/types/invoice";
import { toDisplayDateOnly } from "@helpers/date";
import {
    useIsEntityDateLocked,
    useIsPeriodLockResolved,
} from "@components/periodLock/PeriodLockDatesProvider";

type InvoiceLockInput = Pick<
    InvoiceBase,
    "entityId" | "dateOnly" | "source" | "status" | "externalStatus"
>;

/**
 * True once an invoice's date falls inside a locked period — the same
 * definition `LockCell` uses to show the lock icon on the Invoices and
 * Invoices-by-line-item tables. Drafts are excluded: they haven't posted
 * journal entries yet, so a locked period doesn't block editing them.
 *
 * FE-only for now: this disables the Class / Income account / Receivables
 * account fields client-side. Backend enforcement is a follow-up.
 *
 * While the lock query is still loading the lock dates are unknown, so this
 * fails safe and reports non-draft invoices as locked until it resolves —
 * otherwise the controls would be briefly editable and a submit could persist
 * a change the period lock should have blocked.
 */
export function useIsInvoiceLocked(
    invoice: InvoiceLockInput | null | undefined,
): boolean {
    const isDateLocked = useIsEntityDateLocked(
        invoice?.entityId ?? null,
        toDisplayDateOnly(invoice?.dateOnly),
    );
    const isLockResolved = useIsPeriodLockResolved();

    if (!invoice) {
        return false;
    }

    const isDraft = getInvoiceEffectiveStatus(invoice) === InvoiceStatus.DRAFT;
    if (isDraft) {
        return false;
    }

    return isDateLocked || !isLockResolved;
}
